<template>
  <PrmButton
    ref="button"
    v-tooltip="(shownPresentation === 'inline' || tooltip)
      ? {
        value: tooltip || label,
        position: shownTooltipPosition,
      }
      : undefined"
    class="qmulus-button"
    :class="{
      'qmulus-button_table-button': isIconShownAlone,
      'p-button-icon-only': isIconShownAlone,
    }"
    v-bind="severityMap[presentedSeverity]"
    :text="shownPresentation === 'inline' || severityMap[presentedSeverity].text"
    :title="label"
    :label="isIconShownAlone ? undefined : label"
    :disabled="disabled"
    :loading="loading"
    icon-pos="left"
    :icon="typeof icon === 'string' ? icon : undefined"
    :pt="{
      label: {
        class: {
          'overflow-ellipsis': truncateLabel,
        },
      },
    }"
    :tabindex="isInsideLink ? -1 : undefined"
    :aria-haspopup="ariaHasPopup"
    :aria-controls="ariaControls"
    @click="onClick"
  >
    <template
      v-if="typeof icon === 'object'"
      #icon
    >
      <component :is="icon" />
    </template>
  </PrmButton>
</template>

<script setup lang="ts">
import PrmButton from '@NitrexGroup/primevue/button';
import {
  type ComponentPublicInstance,
  computed,
  onMounted,
  shallowRef,
  useTemplateRef,
} from 'vue';

import {
  TQmulusButtonProps,
  getPresentedSeverity,
  severityMap,
} from './qmulus-button.ts';

// withDefaults would hide from vue-tsc that the icon-only presentations require an icon.
const props = defineProps<TQmulusButtonProps>();

const shownPresentation = computed(() => props.presentation ?? 'labeled');
const presentedSeverity = computed(() => getPresentedSeverity(props.severity ?? 'tertiary', shownPresentation.value));
const shownTooltipPosition = computed(() => props.tooltipPosition ?? 'right');

if (shownPresentation.value !== 'labeled' && !props.icon) {
  throw new Error(`A button presented as ${shownPresentation.value} requires an icon`);
}

const isIconShownAlone = computed(() => shownPresentation.value !== 'labeled');

// A button inside a link, e.g. one opening a row of a table, would be a second stop of the Tab key,
// so the link alone takes the focus and shows it on the button (button/_button.scss).
const buttonComponent = useTemplateRef<ComponentPublicInstance>('button');
const isInsideLink = shallowRef(false);

onMounted(() => {
  const buttonElement: unknown = buttonComponent.value?.$el;
  isInsideLink.value = buttonElement instanceof HTMLElement && buttonElement.parentElement?.closest('a[href]') != null;
});
</script>

<style lang="scss" scoped>
.qmulus-button {
  svg {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
  }

  &_table-button {
    svg {
      margin-right: 0;
    }
  }
}
</style>
